/* eslint-disable @typescript-eslint/naming-convention */
import { Language } from '@por/shared/core';
import { DateFormatType } from './enums/date-format-type.enum';

/**
 * Locale-varying formats are typed as `Record<Language, string>` so a new
 * `Language` member fails the build until its format is supplied, rather than
 * silently falling through to the generic fallback below.
 */
type LocaleFormats = Record<Language, string>;

const dayMonthNameYear: LocaleFormats = {
    [Language.DE_DE]: 'd. MMM yyyy',
    [Language.EN_AU]: 'd MMM, yyyy',
    [Language.EN_GB]: 'd MMM, yyyy',
    [Language.EN_US]: 'MMM d, yyyy',
    [Language.ES_ES]: 'd MMM yyyy',
    [Language.FR_CA]: 'd MMM yyyy',
    [Language.FR_FR]: 'd MMM yyyy'
};

const standardDate: LocaleFormats = {
    [Language.DE_DE]: 'dd.MM.yy',
    [Language.EN_AU]: 'dd/MM/yyyy',
    [Language.EN_GB]: 'dd/MM/yyyy',
    [Language.EN_US]: 'MM/dd/yyyy',
    [Language.ES_ES]: 'dd/MM/yyyy',
    [Language.FR_CA]: 'yyyy-MM-dd',
    [Language.FR_FR]: 'dd/MM/yyyy'
};

const standardDateTime: LocaleFormats = {
    [Language.DE_DE]: 'dd.MM.yy  h:mm a',
    [Language.EN_AU]: 'dd/MM/yyyy h:mm a',
    [Language.EN_GB]: 'dd/MM/yyyy H:mm ',
    [Language.EN_US]: 'MM/dd/yyyy h:mm a',
    [Language.ES_ES]: 'dd/MM/yyyy HH:mm',
    [Language.FR_CA]: 'yyyy-MM-dd h:mm a',
    [Language.FR_FR]: 'dd/MM/yyyy HH:mm'
};

const shortDate: LocaleFormats = {
    [Language.DE_DE]: 'd.M.yy',
    [Language.EN_AU]: 'd/M/yy',
    [Language.EN_GB]: 'd/M/yy',
    [Language.EN_US]: 'M/d/yy',
    [Language.ES_ES]: 'd/M/yy',
    [Language.FR_CA]: 'yy-MM-dd',
    [Language.FR_FR]: 'dd/MM/yy'
};

const shorterDate: LocaleFormats = {
    [Language.DE_DE]: 'dd.MM',
    [Language.EN_AU]: 'dd/MM',
    [Language.EN_GB]: 'dd/MM',
    [Language.EN_US]: 'MM/dd',
    [Language.ES_ES]: 'dd/MM',
    [Language.FR_CA]: 'MM-dd',
    [Language.FR_FR]: 'dd/MM'
};

const short: LocaleFormats = {
    [Language.DE_DE]: 'd.M.yy, HH:mm',
    [Language.EN_AU]: 'd/M/yy, h:mm a',
    [Language.EN_GB]: 'd/M/yy, h:mm a',
    [Language.EN_US]: 'M/d/yy, h:mm a',
    [Language.ES_ES]: 'd/M/yy, HH:mm',
    [Language.FR_CA]: 'yy-MM-dd h:mm a',
    [Language.FR_FR]: 'dd/MM/yy HH:mm'
};

export class DateUtility {
    /**
     * Accepts a descriptive format type and returns the corresponding format for display purposes
     * @param formatType
     * @param locale
     * @returns string
     */
    static getDateDisplayFormat(formatType: DateFormatType, locale: string = Language.EN_US): string {
        const language = DateUtility.isSupportedLocale(locale) ? locale : Language.EN_US;
        return (
            {
                [DateFormatType.MonthNameDayYearTime]: 'MMM d, y, h:mm a',
                [DateFormatType.MonthNameDayYear]: 'MMM d, y',
                [DateFormatType.DayMonthNameYear]: dayMonthNameYear[language],
                [DateFormatType.DayOfWeek]: 'cccc',
                [DateFormatType.ShortTime]: 'h:mm a',
                [DateFormatType.StandardDate]: standardDate[language],
                [DateFormatType.StandardDateTime]: standardDateTime[language],
                [DateFormatType.ShortDate]: shortDate[language],
                [DateFormatType.ShorterDate]: shorterDate[language],
                [DateFormatType.ShortDayOfWeek]: 'ccc',
                [DateFormatType.Short]: short[language]
            }[formatType] || 'yyyy-MM-dd'
        );
    }

    private static isSupportedLocale(locale: string): locale is Language {
        return (Object.values(Language) as string[]).includes(locale);
    }
}
